import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Floating "Install Optivize App" prompt.
 *
 * - Captures the browser's beforeinstallprompt event (Chrome / Edge / Brave).
 * - Shows a dismissible chip in the bottom-right that triggers prompt() on click.
 * - Persists dismissal for 14 days via localStorage so it never nags returning users.
 * - Hides itself when the app is already installed.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Already installed → don't show
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;
    const dismissed = Number(localStorage.getItem("optivize_pwa_dismissed_at") || 0);
    if (dismissed && Date.now() - dismissed < 14 * 24 * 60 * 60 * 1000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show || !deferred) return null;

  const install = async () => {
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome !== "accepted") {
        localStorage.setItem("optivize_pwa_dismissed_at", String(Date.now()));
      }
    } catch { /* ignore */ }
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem("optivize_pwa_dismissed_at", String(Date.now()));
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-5 right-5 z-50 card px-4 py-3 flex items-center gap-3 shadow-2xl border border-blue-500/40 bg-slate-900/95 backdrop-blur"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
        <Download size={18} className="text-white" />
      </div>
      <div className="text-sm">
        <div className="text-white font-semibold">Install Optivize</div>
        <div className="text-slate-400 text-xs">Open like a native app · works offline</div>
      </div>
      <button onClick={install} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="pwa-install-btn">Install</button>
      <button onClick={dismiss} className="text-slate-400 hover:text-white p-1" data-testid="pwa-dismiss-btn"><X size={14} /></button>
    </div>
  );
}
